import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiJson, ApiError } from '@/lib/api';

const COACH_OUTREACH_QUERY_KEY = ['admin', 'coach-outreach-artifact'] as const;
const COACH_OUTREACH_CONTROL_QUERY_KEY = [
  'admin',
  'coach-outreach-control',
] as const;

function parseDetail(err: unknown): string {
  if (err instanceof ApiError && err.bodyText) {
    try {
      const j = JSON.parse(err.bodyText) as { detail?: string | string[] };
      if (typeof j.detail === 'string') return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map(String).join('; ');
    } catch {
      /* ignore */
    }
    return err.bodyText;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

type ConfirmDialogState = {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
};

type CoachOutreachArtifactStatusResponse = {
  artifact_path: string;
  artifact_exists: boolean;
  artifact_contacts: number;
  artifact_emails: number;
  db_contacts: number;
  db_contacts_with_email: number;
  db_profile_only_contacts: number;
  db_email_records: number;
  db_approved_email_records: number;
  db_sent_or_legacy_email_records: number;
};

type CoachOutreachArtifactImportResponse = {
  artifact_path: string;
  artifact_contacts: number;
  artifact_emails: number;
  inserted: number;
  matched_or_enriched: number;
  emails_inserted: number;
  emails_matched_or_updated: number;
  contacts_before: number;
  contacts_after: number;
  emails_before: number;
  emails_after: number;
  dry_run: boolean;
};

type CoachOutreachPriorTouchRow = {
  email_id: number;
  email_key: string;
  status: string;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  bounced_at: string | null;
  complained_at: string | null;
  last_event: string | null;
};

type CoachOutreachDraftRow = {
  contact_id: number;
  coach_name: string;
  email: string | null;
  phone: string | null;
  state: string | null;
  website: string | null;
  profile_url: string | null;
  contact_url: string | null;
  source: string | null;
  summary: string | null;
  notes: string | null;
  contact_status: string;
  tier: number | null;
  email_id: number | null;
  email_key: string;
  email_status: string | null;
  subject: string | null;
  body: string | null;
  sent_at: string | null;
  updated_at: string | null;
  prior_touches: CoachOutreachPriorTouchRow[];
};

type FilterOption = {
  label: string;
  value: string;
  help: string;
};

type StatusFilterOption = FilterOption & {
  /** When set, the filter is only meaningful for these touches and is hidden otherwise. */
  onlyForTouches?: string[];
};

const STATUS_FILTERS: StatusFilterOption[] = [
  {
    label: 'Needs review',
    value: 'needs_review',
    help: 'Drafts and contacts with no draft yet.',
  },
  {
    label: 'Due for follow-up',
    value: 'due',
    help: 'Touch_1 sent ≥3d ago for touch_2, ≥10d ago for touch_3, with no row created yet for the selected touch.',
    onlyForTouches: ['touch_2', 'touch_3'],
  },
  {
    label: 'No draft',
    value: 'none',
    help: 'Contacts that do not yet have copy for this touch.',
  },
  {
    label: 'Draft',
    value: 'draft',
    help: 'Saved drafts that still need review.',
  },
  {
    label: 'Already sent',
    value: 'legacy_sent',
    help: 'Imported emails previously sent from coaches.db.',
  },
  {
    label: 'Sent',
    value: 'sent',
    help: 'Emails sent by the backend through Resend.',
  },
  {
    label: 'Approved',
    value: 'approved',
    help: 'Eligible for Celery once sending is enabled.',
  },
  {
    label: 'Skipped',
    value: 'skipped',
    help: 'Touches a human chose not to send. The Celery sender ignores them.',
  },
  {
    label: 'All',
    value: 'all',
    help: 'All contacts for this touch, regardless of draft status.',
  },
];

const TOUCH_FILTERS: FilterOption[] = [
  {
    label: 'Touch 1',
    value: 'touch_1',
    help: 'First outreach email.',
  },
  {
    label: 'Touch 2',
    value: 'touch_2',
    help: 'Second follow-up touch.',
  },
  {
    label: 'Touch 3',
    value: 'touch_3',
    help: 'Final follow-up touch.',
  },
];

type CoachOutreachDraftListResponse = {
  rows: CoachOutreachDraftRow[];
  total: number;
  limit: number;
  offset: number;
  email_key: string;
};

type CoachOutreachDraftUpsertResponse = {
  email_id: number;
  contact_id: number;
  email_key: string;
  status: string;
  message: string;
};

type CoachOutreachDraftSkipResponse = {
  email_id: number;
  contact_id: number;
  email_key: string;
  status: string;
  message: string;
};

type CoachOutreachContactCreateResponse = {
  contact_id: number;
  coach_name: string;
  email: string;
  source: string;
  status: string;
  message: string;
};

type CoachOutreachControlStatusResponse = {
  enabled: boolean;
  enabled_source: 'redis' | 'env_var' | 'default_off' | string;
  paused: boolean;
  redis_configured: boolean;
  daily_limit: number;
  approved_count: number;
  schedule_environment: 'production' | 'dev' | string;
  schedule_description: string;
  next_run_at_utc: string | null;
  next_run_in_seconds: number | null;
};

type CoachOutreachControlUpdateResponse = {
  enabled: boolean;
  paused: boolean;
  enabled_source: string;
  message: string;
};

type CoachOutreachMacroRow = {
  id: number;
  name: string;
  description: string | null;
  email_key: string | null;
  subject: string;
  body: string;
  created_at: string;
  updated_at: string;
};

type CoachOutreachMacroListResponse = {
  macros: CoachOutreachMacroRow[];
};

type CoachOutreachMacroSaveResponse = {
  macro: CoachOutreachMacroRow;
  created: boolean;
  message: string;
};

type CoachOutreachMacroDeleteResponse = {
  deleted_id: number;
  message: string;
};

type CoachOutreachReplyContactRef = {
  id: number;
  coach_name: string;
  email: string | null;
  status: string;
};

type CoachOutreachReplyRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  has_attachments: boolean;
  matched_contact: CoachOutreachReplyContactRef | null;
};

type CoachOutreachRepliesListResponse = {
  replies: CoachOutreachReplyRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type CoachOutreachReplyDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  cc: string[];
  bcc: string[];
  reply_to: string[];
  subject: string;
  received_at: string | null;
  message_id: string | null;
  html: string | null;
  text: string | null;
  attachments: Array<{
    id?: string;
    filename?: string;
    content_type?: string;
    content_disposition?: string;
  }>;
  matched_contact: CoachOutreachReplyContactRef | null;
};

type CoachOutreachSendRow = {
  id: string;
  from_email: string;
  to_emails: string[];
  subject: string;
  sent_at: string | null;
  last_event: string | null;
  matched_contact: CoachOutreachReplyContactRef | null;
};

type CoachOutreachSendsListResponse = {
  sends: CoachOutreachSendRow[];
  has_more: boolean;
  fetched_at: string;
  error: string | null;
};

type CoachOutreachSendDetailResponse = {
  id: string;
  from_email: string;
  to_emails: string[];
  cc: string[];
  bcc: string[];
  reply_to: string[];
  subject: string;
  sent_at: string | null;
  last_event: string | null;
  html: string | null;
  text: string | null;
  matched_contact: CoachOutreachReplyContactRef | null;
};

const SEND_EVENT_BADGE_CLASSES: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800',
  opened: 'bg-blue-100 text-blue-800',
  clicked: 'bg-emerald-100 text-emerald-900',
  bounced: 'bg-red-100 text-red-800',
  complained: 'bg-red-100 text-red-900',
  failed: 'bg-red-100 text-red-800',
  sent: 'bg-gray-100 text-gray-700',
  queued: 'bg-gray-100 text-gray-700',
  scheduled: 'bg-gray-100 text-gray-700',
};

function sendEventBadgeClass(event: string | null): string {
  if (!event) return 'bg-gray-100 text-gray-600';
  return (
    SEND_EVENT_BADGE_CLASSES[event.toLowerCase()] ?? 'bg-gray-100 text-gray-700'
  );
}

type CoachOutreachPreviewResponse = {
  contact_id: number;
  coach_name: string;
  to_email: string | null;
  from_email: string;
  reply_to: string | null;
  subject: string;
  html: string;
  text: string;
  headers: Record<string, string>;
  unsubscribe_url: string;
};

/** Replace ``{{coach_name}}`` / ``{{first_name}}`` placeholders with values
 * derived from the contact. Other ``{{...}}`` expressions are left as-is so
 * the human can spot and replace them by hand. */
function applyMacroSubstitutions(
  template: string,
  contact: { coach_name: string }
): { value: string; substitutions: string[] } {
  const firstName = (contact.coach_name || '').split(/\s+/)[0] || '';
  const subs = new Set<string>();
  const value = template
    .replace(/\{\{\s*coach_name\s*\}\}/g, () => {
      if (contact.coach_name) subs.add('coach_name');
      return contact.coach_name || '';
    })
    .replace(/\{\{\s*first_name\s*\}\}/g, () => {
      if (firstName) subs.add('first_name');
      return firstName;
    });
  return { value, substitutions: [...subs] };
}

function formatRelativeSeconds(totalSeconds: number): string {
  if (totalSeconds <= 0) return 'now';
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remMinutes = minutes % 60;
    return `${hours}h ${remMinutes}m`;
  }
  if (minutes >= 1) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

function describeEnabledSource(source: string, enabled: boolean): string {
  if (source === 'redis') {
    return enabled
      ? 'Enabled via Redis admin toggle.'
      : 'Disabled via Redis admin toggle (overrides env var).';
  }
  if (source === 'env_var') {
    return 'Enabled via COACH_OUTREACH_ENABLED env var (no Redis override). Toggle below to override in Redis.';
  }
  return 'Disabled — no Redis override and no env var. Click Enable to override in Redis.';
}

type AddTestContactForm = {
  coach_name: string;
  email: string;
  state: string;
  tier: string;
  profile_url: string;
  website: string;
  notes: string;
};

const EMPTY_TEST_CONTACT_FORM: AddTestContactForm = {
  coach_name: '',
  email: '',
  state: '',
  tier: '',
  profile_url: '',
  website: '',
  notes: '',
};

const TOUCH_LABELS: Record<string, string> = {
  touch_1: 'Touch 1',
  touch_2: 'Touch 2',
  touch_3: 'Touch 3',
};

function formatTimestamp(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatRelative(value: string | null): string {
  if (!value) return '—';
  const ts = new Date(value).getTime();
  if (Number.isNaN(ts)) return value;
  const diffMs = Date.now() - ts;
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(value).toLocaleDateString();
}

function summarizePriorEngagement(prior: CoachOutreachPriorTouchRow): string {
  if (!prior.sent_at) return 'Not sent yet.';
  const events: string[] = [];
  if (prior.complained_at) events.push('complained');
  if (prior.bounced_at) events.push('bounced');
  if (prior.clicked_at) events.push('clicked');
  if (prior.opened_at) events.push('opened');
  if (prior.delivered_at && events.length === 0) events.push('delivered');
  if (events.length === 0) return 'Sent. No engagement recorded yet.';
  return `Sent. ${events.join(' · ')}.`;
}

type CoachOutreachDraftGenerateResponse = {
  subject: string;
  body: string;
  model: string;
};

type CoachOutreachProfileSummaryResponse = {
  source_url: string;
  summary: string;
  model: string;
};

function ConfirmDialog({
  open,
  state,
  onClose,
}: {
  open: boolean;
  state: ConfirmDialogState | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !state) return null;

  const confirmClass = state.destructive
    ? 'bg-red-600 text-white hover:bg-red-700'
    : 'bg-primary text-white hover:opacity-90';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
      <div
        className='absolute inset-0 bg-black/40'
        aria-hidden
        onClick={onClose}
      />
      <div
        className='relative z-10 bg-white rounded-lg shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-200'
        role='dialog'
        aria-modal='true'
        aria-labelledby='coach-confirm-dialog-title'
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          id='coach-confirm-dialog-title'
          className='text-lg font-semibold text-gray-900'
        >
          {state.title}
        </h3>
        <p className='text-sm text-gray-600 whitespace-pre-wrap'>
          {state.message}
        </p>
        <div className='flex justify-end gap-2 pt-2'>
          <button
            type='button'
            onClick={onClose}
            className='px-4 py-2 text-sm font-semibold border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={() => {
              state.onConfirm();
              onClose();
            }}
            className={`px-4 py-2 text-sm font-semibold rounded-md ${confirmClass}`}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function CoachOutreachAdminPage() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState | null>(
    null
  );
  const [coachOutreachResult, setCoachOutreachResult] = useState<{
    ok: boolean;
    text: string;
    result?: CoachOutreachArtifactImportResponse;
  } | null>(null);
  const [draftEmailKey, setDraftEmailKey] = useState('touch_1');
  const [draftStatus, setDraftStatus] = useState('needs_review');
  const [draftSearch, setDraftSearch] = useState('');
  const [selectedDraft, setSelectedDraft] =
    useState<CoachOutreachDraftRow | null>(null);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [generateInstructions, setGenerateInstructions] = useState('');
  const [useProfileSummaryForGeneration, setUseProfileSummaryForGeneration] =
    useState(false);
  const [profileSummary, setProfileSummary] = useState<{
    ok: boolean;
    text: string;
    sourceUrl?: string;
    model?: string;
  } | null>(null);
  const [draftBanner, setDraftBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [testContactForm, setTestContactForm] = useState<AddTestContactForm>(
    EMPTY_TEST_CONTACT_FORM
  );
  const [testContactBanner, setTestContactBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const coachOutreachQuery = useQuery({
    queryKey: COACH_OUTREACH_QUERY_KEY,
    queryFn: () =>
      apiJson<CoachOutreachArtifactStatusResponse>(
        getToken,
        '/v1/admin/coach-outreach/artifact'
      ),
  });

  // Refetch the control status periodically so the "next run" countdown stays
  // close to real. 30s is a sweet spot: short enough to feel live, long enough
  // not to thrash Redis on a page that's left open in a tab.
  const controlQuery = useQuery({
    queryKey: COACH_OUTREACH_CONTROL_QUERY_KEY,
    queryFn: () =>
      apiJson<CoachOutreachControlStatusResponse>(
        getToken,
        '/v1/admin/coach-outreach/control'
      ),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
  const [controlBanner, setControlBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  // Local-only seconds-until-next-run countdown so the UI ticks every second
  // between the 30s server refetches.
  const serverNextRunSeconds = controlQuery.data?.next_run_in_seconds ?? null;
  const [secondsUntilNextRun, setSecondsUntilNextRun] = useState<number | null>(
    serverNextRunSeconds
  );
  useEffect(() => {
    setSecondsUntilNextRun(serverNextRunSeconds);
  }, [serverNextRunSeconds]);
  useEffect(() => {
    if (serverNextRunSeconds === null) return;
    const id = window.setInterval(() => {
      setSecondsUntilNextRun((prev) =>
        prev === null ? null : Math.max(0, prev - 1)
      );
    }, 1000);
    return () => window.clearInterval(id);
  }, [serverNextRunSeconds]);

  const repliesQuery = useQuery({
    queryKey: ['admin', 'coach-outreach-replies'] as const,
    queryFn: () =>
      apiJson<CoachOutreachRepliesListResponse>(
        getToken,
        '/v1/admin/coach-outreach/replies?limit=50'
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const [openReplyId, setOpenReplyId] = useState<string | null>(null);
  const [openReplyMode, setOpenReplyMode] = useState<'html' | 'text'>('html');
  const replyDetailQuery = useQuery({
    queryKey: ['admin', 'coach-outreach-reply', openReplyId] as const,
    queryFn: () =>
      apiJson<CoachOutreachReplyDetailResponse>(
        getToken,
        `/v1/admin/coach-outreach/replies/${encodeURIComponent(
          openReplyId ?? ''
        )}`
      ),
    enabled: openReplyId !== null,
  });

  const sendsQuery = useQuery({
    queryKey: ['admin', 'coach-outreach-sends'] as const,
    queryFn: () =>
      apiJson<CoachOutreachSendsListResponse>(
        getToken,
        '/v1/admin/coach-outreach/sends?limit=50'
      ),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const [openSendId, setOpenSendId] = useState<string | null>(null);
  const [openSendMode, setOpenSendMode] = useState<'html' | 'text'>('html');
  const sendDetailQuery = useQuery({
    queryKey: ['admin', 'coach-outreach-send', openSendId] as const,
    queryFn: () =>
      apiJson<CoachOutreachSendDetailResponse>(
        getToken,
        `/v1/admin/coach-outreach/sends/${encodeURIComponent(openSendId ?? '')}`
      ),
    enabled: openSendId !== null,
  });

  const controlMutation = useMutation({
    mutationFn: (patch: { enabled?: boolean; paused?: boolean }) =>
      apiJson<CoachOutreachControlUpdateResponse>(
        getToken,
        '/v1/admin/coach-outreach/control',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        }
      ),
    onSuccess: async (data) => {
      setControlBanner({ ok: true, text: data.message });
      await controlQuery.refetch();
    },
    onError: (e) => setControlBanner({ ok: false, text: parseDetail(e) }),
  });

  const draftParams = new URLSearchParams({
    email_key: draftEmailKey,
    status: draftStatus,
    limit: '50',
    offset: '0',
  });
  if (draftSearch.trim()) draftParams.set('q', draftSearch.trim());

  const draftsQuery = useQuery({
    queryKey: ['admin', 'coach-outreach-drafts', draftParams.toString()],
    queryFn: () =>
      apiJson<CoachOutreachDraftListResponse>(
        getToken,
        `/v1/admin/coach-outreach/drafts?${draftParams.toString()}`
      ),
  });

  const importCoachOutreachMutation = useMutation({
    mutationFn: (dryRun: boolean) =>
      apiJson<CoachOutreachArtifactImportResponse>(
        getToken,
        '/v1/admin/coach-outreach/import-artifact',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dry_run: dryRun }),
        }
      ),
    onSuccess: async (data) => {
      setCoachOutreachResult({
        ok: true,
        text: data.dry_run
          ? `Dry run complete: would insert ${data.inserted} contacts and ${data.emails_inserted} email rows.`
          : `Import complete: inserted ${data.inserted} contacts and ${data.emails_inserted} email rows.`,
        result: data,
      });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);
      await draftsQuery.refetch();
    },
    onError: (e) => setCoachOutreachResult({ ok: false, text: parseDetail(e) }),
  });

  const saveDraftMutation = useMutation({
    mutationFn: (status: 'draft' | 'approved') => {
      if (!selectedDraft) throw new Error('Select a contact first');
      return apiJson<CoachOutreachDraftUpsertResponse>(
        getToken,
        '/v1/admin/coach-outreach/drafts',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft.contact_id,
            email_key: draftEmailKey,
            subject: draftSubject.trim(),
            body: draftBody.trim(),
            status,
          }),
        }
      );
    },
    onSuccess: async (data) => {
      setDraftBanner({ ok: true, text: data.message });
      await draftsQuery.refetch();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);
    },
    onError: (e) => setDraftBanner({ ok: false, text: parseDetail(e) }),
  });

  const generateDraftMutation = useMutation({
    mutationFn: () => {
      if (!selectedDraft) throw new Error('Select a contact first');
      return apiJson<CoachOutreachDraftGenerateResponse>(
        getToken,
        '/v1/admin/coach-outreach/generate-draft',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft.contact_id,
            email_key: draftEmailKey,
            instructions: generateInstructions.trim() || null,
            profile_summary:
              useProfileSummaryForGeneration && profileSummary?.ok
                ? profileSummary.text
                : null,
          }),
        }
      );
    },
    onSuccess: (data) => {
      setDraftSubject(data.subject);
      setDraftBody(data.body);
      setDraftBanner({
        ok: true,
        text: `Generated draft with ${data.model}. Review and save or approve when ready.`,
      });
    },
    onError: (e) => setDraftBanner({ ok: false, text: parseDetail(e) }),
  });

  const createTestContactMutation = useMutation({
    mutationFn: (form: AddTestContactForm) => {
      const tier = form.tier.trim();
      const payload: Record<string, unknown> = {
        coach_name: form.coach_name.trim(),
        email: form.email.trim(),
      };
      if (form.state.trim()) payload.state = form.state.trim();
      if (tier) payload.tier = Number(tier);
      if (form.profile_url.trim()) payload.profile_url = form.profile_url.trim();
      if (form.website.trim()) payload.website = form.website.trim();
      if (form.notes.trim()) payload.notes = form.notes.trim();
      return apiJson<CoachOutreachContactCreateResponse>(
        getToken,
        '/v1/admin/coach-outreach/contacts',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
    },
    onSuccess: async (data) => {
      setTestContactBanner({ ok: true, text: data.message });
      setTestContactForm(EMPTY_TEST_CONTACT_FORM);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);
      await draftsQuery.refetch();
    },
    onError: (e) =>
      setTestContactBanner({ ok: false, text: parseDetail(e) }),
  });

  const macrosQueryKey = [
    'admin',
    'coach-outreach-macros',
    draftEmailKey,
  ] as const;
  const macrosQuery = useQuery({
    queryKey: macrosQueryKey,
    queryFn: () =>
      apiJson<CoachOutreachMacroListResponse>(
        getToken,
        `/v1/admin/coach-outreach/macros?email_key=${encodeURIComponent(
          draftEmailKey
        )}`
      ),
  });

  const [macroSaveName, setMacroSaveName] = useState('');
  const [macroSaveDescription, setMacroSaveDescription] = useState('');
  const [macroSaveScopeToTouch, setMacroSaveScopeToTouch] = useState(true);
  const [macroBanner, setMacroBanner] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const saveMacroMutation = useMutation({
    mutationFn: (payload: {
      name: string;
      description: string | null;
      email_key: string | null;
      subject: string;
      body: string;
    }) =>
      apiJson<CoachOutreachMacroSaveResponse>(
        getToken,
        '/v1/admin/coach-outreach/macros',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: async (data) => {
      setMacroBanner({ ok: true, text: data.message });
      setMacroSaveName('');
      setMacroSaveDescription('');
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'coach-outreach-macros'],
      });
    },
    onError: (e) => setMacroBanner({ ok: false, text: parseDetail(e) }),
  });

  const deleteMacroMutation = useMutation({
    mutationFn: (macroId: number) =>
      apiJson<CoachOutreachMacroDeleteResponse>(
        getToken,
        `/v1/admin/coach-outreach/macros/${macroId}`,
        { method: 'DELETE' }
      ),
    onSuccess: async (data) => {
      setMacroBanner({ ok: true, text: data.message });
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'coach-outreach-macros'],
      });
    },
    onError: (e) => setMacroBanner({ ok: false, text: parseDetail(e) }),
  });

  const handleLoadMacro = (macro: CoachOutreachMacroRow) => {
    if (!selectedDraft) return;
    const subjectResult = applyMacroSubstitutions(macro.subject, selectedDraft);
    const bodyResult = applyMacroSubstitutions(macro.body, selectedDraft);
    setDraftSubject(subjectResult.value);
    setDraftBody(bodyResult.value);
    const subs = [
      ...new Set([
        ...subjectResult.substitutions,
        ...bodyResult.substitutions,
      ]),
    ];
    const quotedName = JSON.stringify(macro.name);
    setMacroBanner({
      ok: true,
      text:
        subs.length > 0
          ? `Loaded macro ${quotedName}. Substituted: ${subs.join(', ')}.`
          : `Loaded macro ${quotedName}. No substitutions.`,
    });
  };

  const [previewResult, setPreviewResult] =
    useState<CoachOutreachPreviewResponse | null>(null);
  const [previewMode, setPreviewMode] = useState<'html' | 'text'>('html');
  const [previewError, setPreviewError] = useState<string | null>(null);

  const previewDraftMutation = useMutation({
    mutationFn: (payload: {
      contact_id: number;
      subject: string;
      body: string;
    }) =>
      apiJson<CoachOutreachPreviewResponse>(
        getToken,
        '/v1/admin/coach-outreach/preview',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: (data) => {
      setPreviewError(null);
      setPreviewMode('html');
      setPreviewResult(data);
    },
    onError: (e) => setPreviewError(parseDetail(e)),
  });

  const skipDraftMutation = useMutation({
    mutationFn: () => {
      if (!selectedDraft) throw new Error('Select a contact first');
      return apiJson<CoachOutreachDraftSkipResponse>(
        getToken,
        '/v1/admin/coach-outreach/skip',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: selectedDraft.contact_id,
            email_key: draftEmailKey,
          }),
        }
      );
    },
    onSuccess: async (data) => {
      setDraftBanner({ ok: true, text: data.message });
      await draftsQuery.refetch();
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_QUERY_KEY,
        }),
        queryClient.invalidateQueries({
          queryKey: COACH_OUTREACH_CONTROL_QUERY_KEY,
        }),
      ]);
    },
    onError: (e) => setDraftBanner({ ok: false, text: parseDetail(e) }),
  });

  const profileSummaryMutation = useMutation({
    mutationFn: (row: CoachOutreachDraftRow) =>
      apiJson<CoachOutreachProfileSummaryResponse>(
        getToken,
        '/v1/admin/coach-outreach/profile-summary',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contact_id: row.contact_id,
          }),
        }
      ),
    onSuccess: (data) => {
      setProfileSummary({
        ok: true,
        text: data.summary,
        sourceUrl: data.source_url,
        model: data.model,
      });
    },
    onError: (e) => {
      setUseProfileSummaryForGeneration(false);
      setProfileSummary({ ok: false, text: parseDetail(e) });
    },
  });

  const handleReviewDraft = (row: CoachOutreachDraftRow) => {
    setSelectedDraft(row);
    setProfileSummary(null);
    setUseProfileSummaryForGeneration(false);
    const url = row.profile_url ?? row.contact_url ?? row.website;
    if (url) {
      profileSummaryMutation.mutate(row);
    } else {
      setProfileSummary({
        ok: false,
        text: 'No profile, contact, or website URL.',
      });
    }
  };

  const coachOutreachStatus = coachOutreachQuery.data;
  const visibleStatusFilters = STATUS_FILTERS.filter(
    (filter) =>
      !filter.onlyForTouches || filter.onlyForTouches.includes(draftEmailKey)
  );
  const selectedStatusFilter =
    visibleStatusFilters.find((filter) => filter.value === draftStatus) ?? null;
  const selectedTouchFilter =
    TOUCH_FILTERS.find((filter) => filter.value === draftEmailKey) ?? null;
  const selectedDraftLocked =
    selectedDraft?.sent_at !== null ||
    selectedDraft?.email_status === 'legacy_sent';
  const selectedDraftAlreadySkipped =
    selectedDraft?.email_status === 'skipped';
  const selectedDraftSentLabel =
    selectedDraft?.email_status === 'legacy_sent'
      ? 'Imported legacy send'
      : selectedDraft?.email_status === 'sent'
      ? 'Sent via Resend'
      : null;
  const priorTouches = selectedDraft?.prior_touches ?? [];

  useEffect(() => {
    if (!selectedDraft) return;
    setDraftSubject(selectedDraft.subject ?? '');
    setDraftBody(selectedDraft.body ?? '');
    setGenerateInstructions('');
    setUseProfileSummaryForGeneration(false);
    setDraftBanner(null);
  }, [selectedDraft]);

  // If the user switches the touch filter to one where the current status
  // filter no longer applies (e.g. picks Touch 1 while "Due for follow-up" is
  // active), reset to the safe default. This avoids sending a 422 to the
  // backend and keeps the UI internally consistent.
  useEffect(() => {
    const stillVisible = STATUS_FILTERS.find(
      (filter) =>
        filter.value === draftStatus &&
        (!filter.onlyForTouches ||
          filter.onlyForTouches.includes(draftEmailKey))
    );
    if (!stillVisible) {
      setDraftStatus('needs_review');
      setSelectedDraft(null);
    }
  }, [draftEmailKey, draftStatus]);

  const control = controlQuery.data;
  const sendingLive = !!control && control.enabled && !control.paused;
  const enabledStatusBadge = control
    ? control.enabled
      ? control.paused
        ? {
            label: 'Enabled, paused',
            className: 'bg-amber-100 text-amber-900 border-amber-200',
          }
        : {
            label: 'Enabled',
            className: 'bg-green-100 text-green-900 border-green-200',
          }
      : {
          label: 'Disabled',
          className: 'bg-red-100 text-red-900 border-red-200',
        }
    : null;

  return (
    <div className='space-y-6'>
      <h1 className='text-2xl font-semibold text-gray-900'>Coach Outreach</h1>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Sender control
            </h2>
            <p className='mt-1 text-sm text-gray-600'>
              Live state of the Celery sender. Toggles are persisted in Redis
              and picked up on the next beat tick — no worker restart needed.
            </p>
          </div>
          <button
            type='button'
            onClick={() => controlQuery.refetch()}
            className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
          >
            Refresh
          </button>
        </div>

        {controlQuery.isLoading ? (
          <p className='text-sm text-gray-500'>Loading sender state…</p>
        ) : controlQuery.isError ? (
          <p className='text-sm text-red-600'>
            {parseDetail(controlQuery.error)}
          </p>
        ) : control ? (
          <>
            <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Status
                </p>
                <p className='mt-1 flex flex-wrap items-center gap-2'>
                  {enabledStatusBadge ? (
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${enabledStatusBadge.className}`}
                    >
                      {enabledStatusBadge.label}
                    </span>
                  ) : null}
                  {sendingLive ? (
                    <span className='text-xs text-green-700 font-semibold'>
                      Will send at next tick
                    </span>
                  ) : (
                    <span className='text-xs text-gray-500'>
                      Next tick will no-op
                    </span>
                  )}
                </p>
                <p className='mt-2 text-xs text-gray-600'>
                  {describeEnabledSource(
                    control.enabled_source,
                    control.enabled
                  )}
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Next Celery run
                </p>
                <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                  {control.schedule_environment === 'production' &&
                  secondsUntilNextRun !== null
                    ? `in ${formatRelativeSeconds(secondsUntilNextRun)}`
                    : control.schedule_description}
                </p>
                <p className='mt-1 text-xs text-gray-500'>
                  {control.schedule_environment === 'production' &&
                  control.next_run_at_utc
                    ? `${new Date(
                        control.next_run_at_utc
                      ).toLocaleString()} (local) · ${control.schedule_description}`
                    : `Environment: ${control.schedule_environment}`}
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Approved &amp; ready
                </p>
                <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                  {control.approved_count.toLocaleString()}
                </p>
                <p className='mt-1 text-xs text-gray-500'>
                  Up to {control.daily_limit} sent per task run
                </p>
              </div>
              <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                  Redis
                </p>
                <p className='mt-1 text-sm text-gray-900'>
                  {control.redis_configured ? 'Configured' : 'Not configured'}
                </p>
                <p className='mt-1 text-xs text-gray-500'>
                  {control.redis_configured
                    ? 'Toggles below are live.'
                    : 'Set REDIS_URL to enable toggle persistence.'}
                </p>
              </div>
            </div>

            {controlBanner ? (
              <div
                className={`rounded-lg border px-4 py-3 text-sm ${
                  controlBanner.ok
                    ? 'bg-green-50 border-green-200 text-green-900'
                    : 'bg-red-50 border-red-200 text-red-800'
                }`}
              >
                {controlBanner.text}
              </div>
            ) : null}

            <div className='flex flex-wrap gap-2'>
              {control.enabled ? (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Disable coach outreach sender?',
                      message:
                        'This sets coach_outreach:enabled in Redis to clear the override (delete the key). The Celery sender will skip the next beat tick. Approved drafts stay queued and will resume sending when you re-enable.',
                      confirmLabel: 'Disable sender',
                      destructive: true,
                      onConfirm: () =>
                        controlMutation.mutate({ enabled: false }),
                    })
                  }
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50'
                >
                  Disable sender
                </button>
              ) : (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Enable coach outreach sender?',
                      message: `This sets coach_outreach:enabled=1 in Redis. The next Celery beat tick will pick up approved drafts (currently ${control.approved_count}, capped at ${control.daily_limit} per run). It does not bypass the pause flag — clear that separately if needed.`,
                      confirmLabel: 'Enable sender',
                      onConfirm: () =>
                        controlMutation.mutate({ enabled: true }),
                    })
                  }
                  className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                >
                  Enable sender
                </button>
              )}
              {control.paused ? (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() => controlMutation.mutate({ paused: false })}
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50'
                >
                  Resume (clear pause)
                </button>
              ) : (
                <button
                  type='button'
                  disabled={
                    controlMutation.isPending || !control.redis_configured
                  }
                  onClick={() =>
                    setConfirmDialog({
                      title: 'Pause coach outreach sender?',
                      message:
                        'Sets coach_outreach:paused=1 in Redis. The next beat tick will no-op until you resume. Use this for a quick hot-pause without disabling the campaign entirely.',
                      confirmLabel: 'Pause sender',
                      onConfirm: () =>
                        controlMutation.mutate({ paused: true }),
                    })
                  }
                  className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                >
                  Pause (hot)
                </button>
              )}
            </div>
          </>
        ) : null}
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-lg font-semibold text-gray-900'>
              Import reviewed snapshot
            </h2>
            <p className='mt-1 text-sm text-gray-600'>
              Replays the reviewed JSONL artifact into{' '}
              <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                coach_outreach_contacts
              </code>{' '}
              and{' '}
              <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                coach_outreach_emails
              </code>
              . This preserves draft/legacy-sent history, but does not approve
              drafts or send email.
            </p>
          </div>
          <button
            type='button'
            onClick={() =>
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: COACH_OUTREACH_QUERY_KEY,
                }),
                queryClient.invalidateQueries({
                  queryKey: COACH_OUTREACH_CONTROL_QUERY_KEY,
                }),
              ])
            }
            className='px-3 py-2 rounded-md border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50'
          >
            Refresh
          </button>
        </div>

        {coachOutreachQuery.isLoading ? (
          <p className='text-sm text-gray-500'>
            Loading coach outreach status...
          </p>
        ) : coachOutreachQuery.isError ? (
          <p className='text-sm text-red-600'>
            {parseDetail(coachOutreachQuery.error)}
          </p>
        ) : coachOutreachStatus ? (
          <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Artifact contacts
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {coachOutreachStatus.artifact_contacts.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {coachOutreachStatus.artifact_exists
                  ? `${coachOutreachStatus.artifact_emails.toLocaleString()} email rows`
                  : 'Artifact missing'}
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                DB contacts
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {coachOutreachStatus.db_contacts.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {coachOutreachStatus.db_contacts_with_email.toLocaleString()}{' '}
                with email
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Needs enrichment
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {coachOutreachStatus.db_profile_only_contacts.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                Profile-only contacts
              </p>
            </div>
            <div className='rounded-lg border border-gray-200 bg-gray-50 p-3'>
              <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                Approved sends
              </p>
              <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
                {coachOutreachStatus.db_approved_email_records.toLocaleString()}
              </p>
              <p className='mt-1 text-xs text-gray-500'>
                {coachOutreachStatus.db_sent_or_legacy_email_records.toLocaleString()}{' '}
                sent or legacy-sent
              </p>
            </div>
          </div>
        ) : (
          <p className='text-sm text-gray-500'>
            No coach outreach status available.
          </p>
        )}

        {coachOutreachResult ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              coachOutreachResult.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            <p>{coachOutreachResult.text}</p>
            {coachOutreachResult.result ? (
              <p className='mt-2 font-mono text-xs opacity-90'>
                contacts: {coachOutreachResult.result.contacts_before} -&gt;{' '}
                {coachOutreachResult.result.contacts_after}
                {' · '}emails: {coachOutreachResult.result.emails_before} -&gt;{' '}
                {coachOutreachResult.result.emails_after}
              </p>
            ) : null}
          </div>
        ) : null}

        <div className='flex flex-wrap gap-3'>
          <button
            type='button'
            disabled={importCoachOutreachMutation.isPending}
            onClick={() => {
              setCoachOutreachResult(null);
              importCoachOutreachMutation.mutate(true);
            }}
            className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
          >
            {importCoachOutreachMutation.isPending
              ? 'Running...'
              : 'Dry run artifact import'}
          </button>
          <button
            type='button'
            disabled={
              importCoachOutreachMutation.isPending ||
              !coachOutreachStatus?.artifact_exists
            }
            onClick={() => {
              setCoachOutreachResult(null);
              setConfirmDialog({
                title: 'Import coach outreach contacts?',
                message:
                  'This will replay the reviewed JSONL artifact into the current database, including legacy draft/sent email rows. It does not approve or send any emails.',
                confirmLabel: 'Import contacts',
                onConfirm: () => importCoachOutreachMutation.mutate(false),
              });
            }}
            className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
          >
            Import artifact
          </button>
        </div>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details className='group'>
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                Replies
                {repliesQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-gray-500'>
                    ({repliesQuery.data.replies.length}
                    {repliesQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Live read-through of Resend Inbound. Click any row to view the
                full message.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-gray-500'>
                {repliesQuery.data?.fetched_at ? (
                  <>
                    Fetched {formatRelative(repliesQuery.data.fetched_at)} from
                    Resend
                  </>
                ) : (
                  'Not fetched yet'
                )}
              </p>
              <button
                type='button'
                onClick={() => repliesQuery.refetch()}
                disabled={repliesQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {repliesQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {repliesQuery.isError ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Couldn't load replies: {parseDetail(repliesQuery.error)}
              </div>
            ) : null}
            {repliesQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {repliesQuery.data.error}
              </div>
            ) : null}

            {repliesQuery.isLoading ? (
              <p className='text-sm text-gray-500'>Loading...</p>
            ) : repliesQuery.data &&
              repliesQuery.data.replies.length === 0 &&
              !repliesQuery.data.error ? (
              <p className='text-sm text-gray-500 italic'>
                No replies yet. Once a coach replies to one of your sends,
                Resend will surface it here.
              </p>
            ) : repliesQuery.data ? (
              <ul className='divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden'>
                {repliesQuery.data.replies.map((reply) => {
                  const matched = reply.matched_contact;
                  return (
                    <li key={reply.id}>
                      <button
                        type='button'
                        onClick={() => {
                          setOpenReplyMode('html');
                          setOpenReplyId(reply.id);
                        }}
                        className='w-full text-left px-3 py-2 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:gap-3'
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-gray-900 truncate'>
                              {reply.from_email}
                            </span>
                            {matched ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'
                                title={`Matched coach_outreach_contacts.id=${matched.id}, status=${matched.status}`}
                              >
                                ✓ {matched.coach_name}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                            {reply.has_attachments ? (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold uppercase tracking-wide'>
                                📎
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-gray-700 truncate'>
                            {reply.subject || '(no subject)'}
                          </p>
                          <p className='text-[11px] text-gray-400 truncate'>
                            to {reply.to_emails.join(', ') || '—'}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-gray-500'>
                          <div>{formatRelative(reply.received_at)}</div>
                          {reply.received_at ? (
                            <div
                              className='text-gray-400'
                              title={formatTimestamp(reply.received_at) ?? ''}
                            >
                              {new Date(reply.received_at).toLocaleString()}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details className='group'>
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                Sends
                {sendsQuery.data ? (
                  <span className='ml-2 text-sm font-normal text-gray-500'>
                    ({sendsQuery.data.sends.length}
                    {sendsQuery.data.has_more ? '+' : ''})
                  </span>
                ) : null}
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Live read-through of Resend Sent. Click any row to view the
                full message and last engagement event.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-3'>
            <div className='flex items-center justify-between gap-2'>
              <p className='text-xs text-gray-500'>
                {sendsQuery.data?.fetched_at ? (
                  <>
                    Fetched {formatRelative(sendsQuery.data.fetched_at)} from
                    Resend
                  </>
                ) : (
                  'Not fetched yet'
                )}
              </p>
              <button
                type='button'
                onClick={() => sendsQuery.refetch()}
                disabled={sendsQuery.isFetching}
                className='px-2.5 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
              >
                {sendsQuery.isFetching ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {sendsQuery.isError ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Couldn't load sends: {parseDetail(sendsQuery.error)}
              </div>
            ) : null}
            {sendsQuery.data?.error ? (
              <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                Resend returned an error: {sendsQuery.data.error}
              </div>
            ) : null}

            {sendsQuery.isLoading ? (
              <p className='text-sm text-gray-500'>Loading...</p>
            ) : sendsQuery.data &&
              sendsQuery.data.sends.length === 0 &&
              !sendsQuery.data.error ? (
              <p className='text-sm text-gray-500 italic'>
                No sends yet on this Resend API key.
              </p>
            ) : sendsQuery.data ? (
              <ul className='divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden'>
                {sendsQuery.data.sends.map((send) => {
                  const matched = send.matched_contact;
                  const primaryTo = send.to_emails[0] ?? '';
                  return (
                    <li key={send.id}>
                      <button
                        type='button'
                        onClick={() => {
                          setOpenSendMode('html');
                          setOpenSendId(send.id);
                        }}
                        className='w-full text-left px-3 py-2 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center sm:gap-3'
                      >
                        <div className='min-w-0 flex-1'>
                          <div className='flex flex-wrap items-baseline gap-2'>
                            <span className='text-sm font-semibold text-gray-900 truncate'>
                              {primaryTo || '(no recipient)'}
                            </span>
                            {matched ? (
                              <span
                                className='inline-flex px-1.5 py-0.5 rounded-full bg-green-100 text-green-800 text-[10px] font-semibold uppercase tracking-wide'
                                title={`Matched coach_outreach_contacts.id=${matched.id}, status=${matched.status}`}
                              >
                                ✓ {matched.coach_name}
                              </span>
                            ) : (
                              <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-semibold uppercase tracking-wide'>
                                no match
                              </span>
                            )}
                            {send.last_event ? (
                              <span
                                className={`inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${sendEventBadgeClass(send.last_event)}`}
                              >
                                {send.last_event}
                              </span>
                            ) : null}
                          </div>
                          <p className='text-sm text-gray-700 truncate'>
                            {send.subject || '(no subject)'}
                          </p>
                          <p className='text-[11px] text-gray-400 truncate'>
                            from {send.from_email || '—'}
                            {send.to_emails.length > 1
                              ? ` · +${send.to_emails.length - 1} more`
                              : ''}
                          </p>
                        </div>
                        <div className='shrink-0 text-right text-[11px] text-gray-500'>
                          <div>{formatRelative(send.sent_at)}</div>
                          {send.sent_at ? (
                            <div
                              className='text-gray-400'
                              title={formatTimestamp(send.sent_at) ?? ''}
                            >
                              {new Date(send.sent_at).toLocaleString()}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
        <details className='group'>
          <summary className='cursor-pointer list-none flex items-start justify-between gap-3'>
            <div>
              <h2 className='text-lg font-semibold text-gray-900'>
                Add test contact
              </h2>
              <p className='mt-1 text-sm text-gray-600'>
                Drop one row into{' '}
                <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                  coach_outreach_contacts
                </code>{' '}
                for end-to-end testing with your own email.
              </p>
            </div>
            <span className='shrink-0 text-xs font-semibold text-gray-500 group-open:hidden'>
              + Expand
            </span>
            <span className='shrink-0 text-xs font-semibold text-gray-500 hidden group-open:inline'>
              − Collapse
            </span>
          </summary>

          <div className='mt-4 space-y-4'>
            <p className='text-sm text-gray-600'>
              Created with{' '}
              <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                source=manual_test
              </code>{' '}
              and{' '}
              <code className='text-xs bg-gray-100 px-1 py-0.5 rounded'>
                status=new
              </code>
              . No email row is created — head to{' '}
              <strong>Touch 1 → Needs review</strong> after submitting to draft
              the first touch.
            </p>
            <p className='text-xs text-amber-700'>
              Heads up: the Celery sender skips contacts whose email matches an
              existing Harbor account (status becomes{' '}
              <code className='bg-amber-100 px-1 rounded'>
                skipped_existing_user
              </code>
              ). Use an email that isn&apos;t signed up to actually receive the
              send.
            </p>

            {testContactBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              testContactBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {testContactBanner.text}
          </div>
        ) : null}

        <form
          className='grid grid-cols-1 sm:grid-cols-2 gap-3'
          onSubmit={(e) => {
            e.preventDefault();
            const trimmedName = testContactForm.coach_name.trim();
            const trimmedEmail = testContactForm.email.trim();
            if (!trimmedName || !trimmedEmail) {
              setTestContactBanner({
                ok: false,
                text: 'Coach name and email are required.',
              });
              return;
            }
            setTestContactBanner(null);
            createTestContactMutation.mutate(testContactForm);
          }}
        >
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Coach name *
            </label>
            <input
              value={testContactForm.coach_name}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  coach_name: e.target.value,
                }))
              }
              placeholder='e.g. Jose (test)'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
              required
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Email *
            </label>
            <input
              type='email'
              value={testContactForm.email}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  email: e.target.value,
                }))
              }
              placeholder='you+coachtest@example.com'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
              required
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              State (optional)
            </label>
            <input
              value={testContactForm.state}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  state: e.target.value,
                }))
              }
              placeholder='CA'
              maxLength={64}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-1'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Tier (optional)
            </label>
            <select
              value={testContactForm.tier}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  tier: e.target.value,
                }))
              }
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white'
            >
              <option value=''>(none)</option>
              <option value='1'>1 (highest priority)</option>
              <option value='2'>2</option>
              <option value='3'>3</option>
            </select>
          </div>
          <div className='sm:col-span-2'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Profile URL (optional)
            </label>
            <input
              value={testContactForm.profile_url}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  profile_url: e.target.value,
                }))
              }
              placeholder='https://example.com/profile/...'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
            <p className='mt-1 text-xs text-gray-500'>
              Used by the LLM draft generator + profile-summary loader. Leave
              blank to test the no-context path.
            </p>
          </div>
          <div className='sm:col-span-2'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Website (optional)
            </label>
            <input
              value={testContactForm.website}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  website: e.target.value,
                }))
              }
              placeholder='https://example.com'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-2'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Notes (optional)
            </label>
            <textarea
              value={testContactForm.notes}
              onChange={(e) =>
                setTestContactForm((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              rows={2}
              placeholder='Free text, e.g. "test contact for QA, May 2026"'
              maxLength={2000}
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
          <div className='sm:col-span-2 flex flex-wrap gap-2'>
            <button
              type='submit'
              disabled={createTestContactMutation.isPending}
              className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
            >
              {createTestContactMutation.isPending
                ? 'Adding...'
                : 'Add test contact'}
            </button>
            <button
              type='button'
              disabled={createTestContactMutation.isPending}
              onClick={() => {
                setTestContactForm(EMPTY_TEST_CONTACT_FORM);
                setTestContactBanner(null);
              }}
              className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
            >
              Reset
            </button>
          </div>
        </form>
          </div>
        </details>
      </section>

      <section className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4'>
        <div>
          <h2 className='text-lg font-semibold text-gray-900'>
            Draft review and sent history
          </h2>
          <p className='mt-1 text-sm text-gray-600'>
            Review or write one touch at a time. Saving keeps the row as a
            draft; approving makes it eligible for Celery once coach outreach
            sending is enabled. Use Already sent to inspect imported legacy
            emails from coaches.db.
          </p>
        </div>

        <div className='space-y-3'>
          <div>
            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Status
            </p>
            <div className='flex flex-wrap gap-2'>
              {visibleStatusFilters.map((filter) => (
                <button
                  key={filter.value}
                  type='button'
                  onClick={() => {
                    setDraftStatus(filter.value);
                    setSelectedDraft(null);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                    draftStatus === filter.value
                      ? 'border-primary bg-harbor-50 text-primary'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={filter.help}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className='mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500'>
              Touch
            </p>
            <div className='flex flex-wrap gap-2'>
              {TOUCH_FILTERS.map((filter) => (
                <button
                  key={filter.value}
                  type='button'
                  onClick={() => {
                    setDraftEmailKey(filter.value);
                    setSelectedDraft(null);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold border ${
                    draftEmailKey === filter.value
                      ? 'border-primary bg-harbor-50 text-primary'
                      : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                  title={filter.help}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        {selectedStatusFilter || selectedTouchFilter ? (
          <p className='text-xs text-gray-500'>
            {selectedStatusFilter?.help}
            {selectedStatusFilter && selectedTouchFilter ? ' ' : ''}
            {selectedTouchFilter?.help}
          </p>
        ) : null}

        <div className='flex flex-wrap gap-3 items-end'>
          <div className='flex-1 min-w-[220px]'>
            <label className='block text-xs font-semibold text-gray-600 mb-1'>
              Search
            </label>
            <input
              value={draftSearch}
              onChange={(e) => {
                setDraftSearch(e.target.value);
                setSelectedDraft(null);
              }}
              placeholder='Coach name, email, state, source'
              className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
            />
          </div>
        </div>

        {draftBanner ? (
          <div
            className={`rounded-lg border px-4 py-3 text-sm ${
              draftBanner.ok
                ? 'bg-green-50 border-green-200 text-green-900'
                : 'bg-red-50 border-red-200 text-red-800'
            }`}
          >
            {draftBanner.text}
          </div>
        ) : null}

        <div className='grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(360px,520px)] gap-4'>
          <div className='border border-gray-200 rounded-md overflow-hidden'>
            <table className='w-full text-sm'>
              <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
                <tr>
                  <th scope='col' className='px-4 py-3 text-left'>
                    Coach
                  </th>
                  <th scope='col' className='px-4 py-3 text-left'>
                    Email
                  </th>
                  <th scope='col' className='px-4 py-3 text-left'>
                    Draft status
                  </th>
                  <th scope='col' className='px-4 py-3 text-left'>
                    Sent
                  </th>
                  <th scope='col' className='px-4 py-3 text-right'>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-gray-100'>
                {draftsQuery.isLoading && (
                  <tr>
                    <td
                      colSpan={5}
                      className='px-4 py-8 text-center text-gray-400'
                    >
                      Loading drafts...
                    </td>
                  </tr>
                )}
                {draftsQuery.isError && (
                  <tr>
                    <td
                      colSpan={5}
                      className='px-4 py-8 text-center text-red-600'
                    >
                      {parseDetail(draftsQuery.error)}
                    </td>
                  </tr>
                )}
                {!draftsQuery.isLoading &&
                  !draftsQuery.isError &&
                  (draftsQuery.data?.rows ?? []).map((row) => {
                    const active = selectedDraft?.contact_id === row.contact_id;
                    const locked =
                      row.sent_at !== null ||
                      row.email_status === 'legacy_sent';
                    return (
                      <tr
                        key={`${row.contact_id}-${row.email_key}`}
                        className={active ? 'bg-harbor-50' : 'hover:bg-gray-50'}
                      >
                        <td className='px-4 py-2'>
                          <p className='font-medium text-gray-900'>
                            {row.coach_name}
                          </p>
                          <p className='text-xs text-gray-500'>
                            {row.state ?? '-'} ·{' '}
                            {row.source ?? 'unknown source'}
                          </p>
                        </td>
                        <td className='px-4 py-2 text-gray-700'>
                          {row.email ?? (
                            <span className='text-amber-700'>Needs email</span>
                          )}
                        </td>
                        <td className='px-4 py-2'>
                          <span className='inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs font-semibold'>
                            {row.email_status ?? 'none'}
                          </span>
                          {locked ? (
                            <span className='ml-2 text-xs text-gray-500'>
                              locked
                            </span>
                          ) : null}
                        </td>
                        <td className='px-4 py-2 text-xs text-gray-600'>
                          {row.sent_at
                            ? new Date(row.sent_at).toLocaleDateString()
                            : '-'}
                        </td>
                        <td className='px-4 py-2 text-right'>
                          <button
                            type='button'
                            onClick={() => handleReviewDraft(row)}
                            className='px-3 py-1.5 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-white'
                          >
                            Review
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                {!draftsQuery.isLoading &&
                  !draftsQuery.isError &&
                  (draftsQuery.data?.rows ?? []).length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className='px-4 py-8 text-center text-gray-400'
                      >
                        No matching coach outreach drafts.
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

          <div className='border border-gray-200 rounded-md p-4 space-y-3'>
            {selectedDraft ? (
              <>
                <div>
                  <p className='text-sm font-semibold text-gray-900'>
                    {selectedDraft.coach_name}
                  </p>
                  <p className='text-xs text-gray-500'>
                    {selectedDraft.email ?? 'No email on contact'} ·{' '}
                    {draftEmailKey}
                  </p>
                </div>
                <div className='rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-2'>
                  <div className='flex flex-wrap gap-2'>
                    {selectedDraft.website ? (
                      <a
                        href={selectedDraft.website}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary font-semibold hover:underline'
                      >
                        Website
                      </a>
                    ) : null}
                    {selectedDraft.profile_url ? (
                      <a
                        href={selectedDraft.profile_url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary font-semibold hover:underline'
                      >
                        Directory profile
                      </a>
                    ) : null}
                    {selectedDraft.contact_url ? (
                      <a
                        href={selectedDraft.contact_url}
                        target='_blank'
                        rel='noreferrer'
                        className='text-primary font-semibold hover:underline'
                      >
                        Contact page
                      </a>
                    ) : null}
                  </div>
                  <dl className='grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs'>
                    <div>
                      <dt className='font-semibold text-gray-500'>State</dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.state ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>Phone</dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.phone ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>Source</dt>
                      <dd className='text-gray-800 break-all'>
                        {selectedDraft.source ?? '-'}
                      </dd>
                    </div>
                    <div>
                      <dt className='font-semibold text-gray-500'>
                        Tier / status
                      </dt>
                      <dd className='text-gray-800'>
                        {selectedDraft.tier ?? '-'} /{' '}
                        {selectedDraft.contact_status}
                      </dd>
                    </div>
                  </dl>
                  {selectedDraft.summary ? (
                    <p className='text-xs text-gray-700 whitespace-pre-wrap'>
                      {selectedDraft.summary}
                    </p>
                  ) : null}
                  {selectedDraft.notes ? (
                    <p className='text-xs text-gray-500 whitespace-pre-wrap'>
                      Notes: {selectedDraft.notes}
                    </p>
                  ) : null}
                </div>
                <div className='rounded-lg border border-gray-200 p-3 space-y-2'>
                  <div className='flex items-center justify-between gap-2'>
                    <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                      Loaded profile summary
                    </p>
                    <button
                      type='button'
                      disabled={profileSummaryMutation.isPending}
                      onClick={() =>
                        profileSummaryMutation.mutate(selectedDraft)
                      }
                      className='text-xs font-semibold text-primary hover:underline disabled:opacity-50'
                    >
                      {profileSummaryMutation.isPending
                        ? 'Loading...'
                        : 'Reload'}
                    </button>
                  </div>
                  {profileSummaryMutation.isPending ? (
                    <p className='text-sm text-gray-500'>
                      Loading and summarizing profile...
                    </p>
                  ) : profileSummary ? (
                    <div
                      className={`text-sm whitespace-pre-wrap ${
                        profileSummary.ok ? 'text-gray-800' : 'text-red-700'
                      }`}
                    >
                      <p>{profileSummary.text}</p>
                      {profileSummary.ok && profileSummary.sourceUrl ? (
                        <p className='mt-2 text-xs text-gray-500'>
                          Summarized from{' '}
                          <a
                            href={profileSummary.sourceUrl}
                            target='_blank'
                            rel='noreferrer'
                            className='text-primary hover:underline'
                          >
                            source page
                          </a>
                          {profileSummary.model
                            ? ` using ${profileSummary.model}`
                            : ''}
                          .
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <p className='text-sm text-gray-500'>
                      Click Review to load the coach profile summary.
                    </p>
                  )}
                </div>
                {selectedDraftSentLabel ? (
                  <div className='rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900'>
                    <p className='font-semibold'>{selectedDraftSentLabel}</p>
                    <p className='text-xs'>
                      {selectedDraft.sent_at
                        ? `Sent ${new Date(
                            selectedDraft.sent_at
                          ).toLocaleString()}`
                        : 'Sent timestamp unavailable'}
                    </p>
                  </div>
                ) : null}
                {selectedDraftAlreadySkipped ? (
                  <div className='rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900'>
                    <p className='font-semibold'>This touch is skipped</p>
                    <p className='text-xs'>
                      The Celery sender will not pick this up. Save as a draft
                      or approve to revive it.
                    </p>
                  </div>
                ) : null}
                {priorTouches.length > 0 ? (
                  <div className='rounded-lg border border-gray-200 p-3 space-y-3'>
                    <div className='flex items-center justify-between'>
                      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                        Previous touches for this coach
                      </p>
                      <p className='text-xs text-gray-400'>
                        {priorTouches.length} of 2 other touches
                      </p>
                    </div>
                    <div className='space-y-3'>
                      {priorTouches.map((prior) => {
                        const sentLabel = formatTimestamp(prior.sent_at);
                        const engagement = summarizePriorEngagement(prior);
                        return (
                          <details
                            key={prior.email_id || prior.email_key}
                            className='border border-gray-200 rounded-md bg-gray-50'
                          >
                            <summary className='cursor-pointer list-none px-3 py-2 flex flex-wrap items-center gap-2'>
                              <span className='text-sm font-semibold text-gray-900'>
                                {TOUCH_LABELS[prior.email_key] ??
                                  prior.email_key}
                              </span>
                              <span className='inline-flex px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-xs font-semibold'>
                                {prior.status}
                              </span>
                              {sentLabel ? (
                                <span className='text-xs text-gray-500'>
                                  {sentLabel}
                                </span>
                              ) : null}
                              {prior.last_event ? (
                                <span className='text-xs text-gray-500'>
                                  · last event: {prior.last_event}
                                </span>
                              ) : null}
                              <span className='ml-auto text-xs text-gray-500'>
                                {engagement}
                              </span>
                            </summary>
                            <div className='border-t border-gray-200 px-3 py-2 space-y-2 bg-white rounded-b-md'>
                              <div>
                                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                                  Subject
                                </p>
                                <p className='text-sm text-gray-900 break-words'>
                                  {prior.subject?.trim() || '(no subject)'}
                                </p>
                              </div>
                              <div>
                                <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
                                  Body
                                </p>
                                <pre className='text-sm text-gray-800 whitespace-pre-wrap font-sans'>
                                  {prior.body?.trim() || '(no body)'}
                                </pre>
                              </div>
                              <dl className='grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs text-gray-600'>
                                {[
                                  ['Delivered', prior.delivered_at],
                                  ['Opened', prior.opened_at],
                                  ['Clicked', prior.clicked_at],
                                  ['Bounced', prior.bounced_at],
                                  ['Complained', prior.complained_at],
                                ].map(([label, value]) =>
                                  value ? (
                                    <div key={label as string}>
                                      <dt className='font-semibold text-gray-500'>
                                        {label}
                                      </dt>
                                      <dd>{formatTimestamp(value)}</dd>
                                    </div>
                                  ) : null
                                )}
                              </dl>
                            </div>
                          </details>
                        );
                      })}
                    </div>
                  </div>
                ) : null}
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Subject
                  </label>
                  <input
                    value={draftSubject}
                    onChange={(e) => setDraftSubject(e.target.value)}
                    readOnly={selectedDraftLocked}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm read-only:bg-gray-50'
                  />
                </div>
                <div>
                  <label className='block text-xs font-semibold text-gray-600 mb-1'>
                    Body
                  </label>
                  <textarea
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    readOnly={selectedDraftLocked}
                    rows={12}
                    className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-mono read-only:bg-gray-50'
                  />
                  <p className='mt-1 text-xs text-gray-500'>
                    URLs and emails are auto-linked. Use markdown{' '}
                    <code className='bg-gray-100 px-1 rounded'>
                      [label](https://example.com)
                    </code>{' '}
                    when you want custom link text.
                  </p>
                </div>
                <div className='rounded-lg border border-gray-200 p-3 space-y-3'>
                  <div className='flex items-baseline justify-between gap-2'>
                    <div>
                      <p className='text-sm font-semibold text-gray-900'>
                        Macros
                      </p>
                      <p className='text-xs text-gray-500'>
                        Reusable subject + body templates. Supports{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{coach_name}}'}
                        </code>{' '}
                        and{' '}
                        <code className='bg-gray-100 px-1 rounded'>
                          {'{{first_name}}'}
                        </code>
                        .
                      </p>
                    </div>
                    {macrosQuery.isFetching ? (
                      <span className='text-xs text-gray-400'>
                        Loading...
                      </span>
                    ) : null}
                  </div>

                  {macroBanner ? (
                    <div
                      className={`rounded-md border px-3 py-2 text-xs ${
                        macroBanner.ok
                          ? 'bg-green-50 border-green-200 text-green-900'
                          : 'bg-red-50 border-red-200 text-red-800'
                      }`}
                    >
                      {macroBanner.text}
                    </div>
                  ) : null}

                  {macrosQuery.data && macrosQuery.data.macros.length > 0 ? (
                    <ul className='space-y-1.5'>
                      {macrosQuery.data.macros.map((macro) => {
                        const scopeLabel =
                          macro.email_key === null
                            ? 'Any touch'
                            : TOUCH_LABELS[macro.email_key] ?? macro.email_key;
                        return (
                          <li
                            key={macro.id}
                            className='flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5'
                          >
                            <div className='flex-1 min-w-0'>
                              <div className='flex flex-wrap items-baseline gap-2'>
                                <span className='text-sm font-semibold text-gray-900 truncate'>
                                  {macro.name}
                                </span>
                                <span className='inline-flex px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[10px] font-semibold uppercase tracking-wide'>
                                  {scopeLabel}
                                </span>
                              </div>
                              {macro.description ? (
                                <p className='text-xs text-gray-600 mt-0.5'>
                                  {macro.description}
                                </p>
                              ) : null}
                              <p className='text-[11px] text-gray-400 mt-0.5 truncate'>
                                {macro.subject || '(no subject)'}
                              </p>
                            </div>
                            <div className='flex gap-1 shrink-0'>
                              <button
                                type='button'
                                disabled={selectedDraftLocked}
                                onClick={() => handleLoadMacro(macro)}
                                className='px-2 py-1 text-xs font-semibold rounded border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50 disabled:cursor-not-allowed'
                                title={
                                  selectedDraftLocked
                                    ? 'Editor is locked (already sent)'
                                    : 'Load into editor'
                                }
                              >
                                Load
                              </button>
                              <button
                                type='button'
                                disabled={
                                  deleteMacroMutation.isPending &&
                                  deleteMacroMutation.variables === macro.id
                                }
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      `Delete macro "${macro.name}"? This cannot be undone.`
                                    )
                                  ) {
                                    deleteMacroMutation.mutate(macro.id);
                                  }
                                }}
                                className='px-2 py-1 text-xs font-semibold rounded border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50'
                                title='Delete macro'
                              >
                                ×
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className='text-xs text-gray-500 italic'>
                      No macros saved yet for this touch. Save the current
                      draft below to reuse it later.
                    </p>
                  )}

                  <details className='group'>
                    <summary className='cursor-pointer list-none text-xs font-semibold text-primary hover:underline'>
                      <span className='group-open:hidden'>
                        + Save current draft as macro
                      </span>
                      <span className='hidden group-open:inline'>
                        − Hide save form
                      </span>
                    </summary>
                    <form
                      className='mt-2 space-y-2'
                      onSubmit={(e) => {
                        e.preventDefault();
                        const trimmedName = macroSaveName.trim();
                        if (!trimmedName) {
                          setMacroBanner({
                            ok: false,
                            text: 'Macro name is required.',
                          });
                          return;
                        }
                        if (!draftSubject.trim() || !draftBody.trim()) {
                          setMacroBanner({
                            ok: false,
                            text:
                              'Subject and body must both have content before saving as a macro.',
                          });
                          return;
                        }
                        setMacroBanner(null);
                        saveMacroMutation.mutate({
                          name: trimmedName,
                          description:
                            macroSaveDescription.trim() || null,
                          email_key: macroSaveScopeToTouch
                            ? draftEmailKey
                            : null,
                          subject: draftSubject,
                          body: draftBody,
                        });
                      }}
                    >
                      <div>
                        <label className='block text-[11px] font-semibold text-gray-600 mb-1'>
                          Macro name (used as the unique key &mdash; saving
                          with the same name overwrites)
                        </label>
                        <input
                          value={macroSaveName}
                          onChange={(e) => setMacroSaveName(e.target.value)}
                          placeholder='e.g. Touch 2 - soft followup'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                      <div>
                        <label className='block text-[11px] font-semibold text-gray-600 mb-1'>
                          Description (optional)
                        </label>
                        <input
                          value={macroSaveDescription}
                          onChange={(e) =>
                            setMacroSaveDescription(e.target.value)
                          }
                          placeholder='When to use this macro'
                          className='w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm'
                        />
                      </div>
                      <label className='flex items-start gap-2 text-xs text-gray-700'>
                        <input
                          type='checkbox'
                          checked={macroSaveScopeToTouch}
                          onChange={(e) =>
                            setMacroSaveScopeToTouch(e.target.checked)
                          }
                          className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary'
                        />
                        <span>
                          Scope to{' '}
                          <strong>
                            {TOUCH_LABELS[draftEmailKey] ?? draftEmailKey}
                          </strong>{' '}
                          only (uncheck to make available on all touches)
                        </span>
                      </label>
                      <button
                        type='submit'
                        disabled={saveMacroMutation.isPending}
                        className='px-3 py-1.5 text-xs font-semibold rounded-md border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50'
                      >
                        {saveMacroMutation.isPending
                          ? 'Saving...'
                          : 'Save macro'}
                      </button>
                    </form>
                  </details>
                </div>
                {!selectedDraftLocked ? (
                  <div className='rounded-lg border border-gray-200 p-3 space-y-2'>
                    <label className='block text-xs font-semibold text-gray-600'>
                      LLM generation notes (optional)
                    </label>
                    <textarea
                      value={generateInstructions}
                      onChange={(e) => setGenerateInstructions(e.target.value)}
                      rows={2}
                      placeholder='e.g. emphasize co-parenting plans, make it shorter, mention free trial'
                      className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
                    />
                    <label className='flex items-start gap-2 text-xs text-gray-700'>
                      <input
                        type='checkbox'
                        checked={useProfileSummaryForGeneration}
                        disabled={!profileSummary?.ok}
                        onChange={(e) =>
                          setUseProfileSummaryForGeneration(e.target.checked)
                        }
                        className='mt-0.5 h-4 w-4 rounded border-gray-300 text-primary disabled:opacity-50'
                      />
                      <span>
                        Let the LLM use the loaded profile summary
                        {!profileSummary?.ok
                          ? ' (enabled after the summary loads successfully)'
                          : ''}
                      </span>
                    </label>
                    <button
                      type='button'
                      disabled={generateDraftMutation.isPending}
                      onClick={() => generateDraftMutation.mutate()}
                      className='px-4 py-2 text-sm font-semibold rounded-md border border-primary text-primary hover:bg-harbor-50 disabled:opacity-50'
                    >
                      {generateDraftMutation.isPending
                        ? 'Generating...'
                        : draftSubject || draftBody
                        ? 'Regenerate with LLM'
                        : 'Generate with LLM'}
                    </button>
                    <p className='text-xs text-gray-500'>
                      Generation only fills the editor. It does not save,
                      approve, or send.
                    </p>
                  </div>
                ) : null}
                <div className='flex flex-wrap gap-2'>
                  <button
                    type='button'
                    disabled={
                      saveDraftMutation.isPending ||
                      selectedDraft.sent_at !== null ||
                      selectedDraft.email_status === 'legacy_sent'
                    }
                    onClick={() => saveDraftMutation.mutate('draft')}
                    className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                  >
                    Save draft
                  </button>
                  <button
                    type='button'
                    disabled={
                      previewDraftMutation.isPending ||
                      !draftSubject.trim() ||
                      !draftBody.trim()
                    }
                    onClick={() => {
                      setPreviewError(null);
                      previewDraftMutation.mutate({
                        contact_id: selectedDraft.contact_id,
                        subject: draftSubject,
                        body: draftBody,
                      });
                    }}
                    className='px-4 py-2 text-sm font-semibold rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50'
                    title='Render the email exactly as the Celery sender will produce it (footer, unsubscribe link, headers).'
                  >
                    {previewDraftMutation.isPending
                      ? 'Rendering...'
                      : 'Preview'}
                  </button>
                  <button
                    type='button'
                    disabled={
                      saveDraftMutation.isPending ||
                      selectedDraft.sent_at !== null ||
                      selectedDraft.email_status === 'legacy_sent' ||
                      !selectedDraft.email
                    }
                    onClick={() =>
                      setConfirmDialog({
                        title: 'Approve coach outreach draft?',
                        message:
                          'Approved drafts become eligible for the Celery sender once it is enabled (see Sender control above). This click does not send immediately.',
                        confirmLabel: 'Approve draft',
                        onConfirm: () => saveDraftMutation.mutate('approved'),
                      })
                    }
                    className='px-4 py-2 text-sm font-semibold rounded-md bg-primary text-white disabled:opacity-50'
                  >
                    Approve
                  </button>
                  <button
                    type='button'
                    disabled={
                      skipDraftMutation.isPending ||
                      selectedDraft.sent_at !== null ||
                      selectedDraft.email_status === 'legacy_sent' ||
                      selectedDraftAlreadySkipped
                    }
                    onClick={() =>
                      setConfirmDialog({
                        title: 'Skip this touch?',
                        message:
                          'Marks this touch as intentionally skipped so it stops showing up under "Needs review" / "Due for follow-up". The Celery sender already ignores skipped rows. You can un-skip later by saving as a draft.',
                        confirmLabel: 'Skip touch',
                        onConfirm: () => skipDraftMutation.mutate(),
                      })
                    }
                    className='px-4 py-2 text-sm font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50'
                  >
                    {selectedDraftAlreadySkipped
                      ? 'Already skipped'
                      : 'Skip this touch'}
                  </button>
                </div>
                {previewError ? (
                  <div className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
                    Preview failed: {previewError}
                  </div>
                ) : null}
              </>
            ) : (
              <p className='text-sm text-gray-500'>
                Select a coach to review, edit, save, or approve draft copy.
              </p>
            )}
          </div>
        </div>
      </section>

      <ConfirmDialog
        open={confirmDialog !== null}
        state={confirmDialog}
        onClose={() => setConfirmDialog(null)}
      />

      <PreviewDialog
        result={previewResult}
        mode={previewMode}
        onModeChange={setPreviewMode}
        onClose={() => setPreviewResult(null)}
      />

      <ReplyDialog
        open={openReplyId !== null}
        loading={replyDetailQuery.isLoading}
        error={
          replyDetailQuery.isError
            ? parseDetail(replyDetailQuery.error)
            : null
        }
        reply={replyDetailQuery.data ?? null}
        mode={openReplyMode}
        onModeChange={setOpenReplyMode}
        onClose={() => setOpenReplyId(null)}
      />

      <SendDialog
        open={openSendId !== null}
        loading={sendDetailQuery.isLoading}
        error={
          sendDetailQuery.isError ? parseDetail(sendDetailQuery.error) : null
        }
        send={sendDetailQuery.data ?? null}
        mode={openSendMode}
        onModeChange={setOpenSendMode}
        onClose={() => setOpenSendId(null)}
      />
    </div>
  );
}

function SendDialog({
  open,
  loading,
  error,
  send,
  mode,
  onModeChange,
  onClose,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  send: CoachOutreachSendDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Sent email
              {send?.matched_contact ? (
                <span className='ml-2 text-sm font-normal text-gray-600'>
                  to {send.matched_contact.coach_name}
                </span>
              ) : null}
              {send?.last_event ? (
                <span
                  className={`ml-2 inline-flex px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${sendEventBadgeClass(send.last_event)}`}
                >
                  {send.last_event}
                </span>
              ) : null}
            </h2>
            {send?.sent_at ? (
              <p className='mt-0.5 text-xs text-gray-500'>
                Sent {formatRelative(send.sent_at)} ·{' '}
                {formatTimestamp(send.sent_at)}
              </p>
            ) : null}
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
          >
            Close
          </button>
        </header>

        {loading ? (
          <div className='p-6 text-sm text-gray-500'>Loading send...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : send ? (
          <>
            <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>From</span>
                <span className='truncate'>{send.from_email || '—'}</span>
              </div>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>To</span>
                <span className='truncate'>
                  {send.to_emails.join(', ') || '—'}
                </span>
              </div>
              <div className='flex gap-2 sm:col-span-2'>
                <span className='font-semibold text-gray-500 w-16'>
                  Subject
                </span>
                <span className='truncate'>
                  {send.subject || '(no subject)'}
                </span>
              </div>
              {send.reply_to.length > 0 ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Reply-To
                  </span>
                  <span className='truncate'>{send.reply_to.join(', ')}</span>
                </div>
              ) : null}
              {send.matched_contact ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Matched
                  </span>
                  <span className='truncate'>
                    {send.matched_contact.coach_name} ·{' '}
                    <code className='text-[11px] bg-gray-100 px-1 rounded'>
                      contact #{send.matched_contact.id} ·{' '}
                      status={send.matched_contact.status}
                    </code>
                  </span>
                </div>
              ) : null}
            </div>

            <div className='border-b border-gray-200 px-5 pt-3'>
              <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  disabled={!send.html}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  disabled={!send.text}
                  className={`px-3 py-1.5 border-l border-gray-300 ${
                    mode === 'text'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
              {mode === 'html' && send.html ? (
                <iframe
                  title='Send HTML'
                  srcDoc={send.html}
                  sandbox=''
                  className='w-full h-[60vh] bg-white rounded shadow border border-gray-200'
                />
              ) : send.text ? (
                <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800'>
                  {send.text}
                </pre>
              ) : (
                <p className='text-sm text-gray-500 italic'>
                  No body returned by Resend for this send.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function ReplyDialog({
  open,
  loading,
  error,
  reply,
  mode,
  onModeChange,
  onClose,
}: {
  open: boolean;
  loading: boolean;
  error: string | null;
  reply: CoachOutreachReplyDetailResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Reply
              {reply?.matched_contact ? (
                <span className='ml-2 text-sm font-normal text-gray-600'>
                  from {reply.matched_contact.coach_name}
                </span>
              ) : null}
            </h2>
            {reply?.received_at ? (
              <p className='mt-0.5 text-xs text-gray-500'>
                Received {formatRelative(reply.received_at)} ·{' '}
                {formatTimestamp(reply.received_at)}
              </p>
            ) : null}
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
          >
            Close
          </button>
        </header>

        {loading ? (
          <div className='p-6 text-sm text-gray-500'>Loading reply...</div>
        ) : error ? (
          <div className='m-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800'>
            {error}
          </div>
        ) : reply ? (
          <>
            <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>From</span>
                <span className='truncate'>{reply.from_email}</span>
              </div>
              <div className='flex gap-2'>
                <span className='font-semibold text-gray-500 w-16'>To</span>
                <span className='truncate'>
                  {reply.to_emails.join(', ') || '—'}
                </span>
              </div>
              <div className='flex gap-2 sm:col-span-2'>
                <span className='font-semibold text-gray-500 w-16'>
                  Subject
                </span>
                <span className='truncate'>
                  {reply.subject || '(no subject)'}
                </span>
              </div>
              {reply.matched_contact ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Matched
                  </span>
                  <span className='truncate'>
                    {reply.matched_contact.coach_name} ·{' '}
                    <code className='text-[11px] bg-gray-100 px-1 rounded'>
                      contact #{reply.matched_contact.id} ·{' '}
                      status={reply.matched_contact.status}
                    </code>
                  </span>
                </div>
              ) : null}
              {reply.attachments.length > 0 ? (
                <div className='flex gap-2 sm:col-span-2'>
                  <span className='font-semibold text-gray-500 w-16'>
                    Attach.
                  </span>
                  <span className='truncate'>
                    {reply.attachments
                      .map(
                        (a) =>
                          a.filename ||
                          a.content_type ||
                          a.id ||
                          '(unnamed)'
                      )
                      .join(', ')}
                  </span>
                </div>
              ) : null}
            </div>

            <div className='border-b border-gray-200 px-5 pt-3'>
              <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
                <button
                  type='button'
                  onClick={() => onModeChange('html')}
                  disabled={!reply.html}
                  className={`px-3 py-1.5 ${
                    mode === 'html'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  HTML
                </button>
                <button
                  type='button'
                  onClick={() => onModeChange('text')}
                  disabled={!reply.text}
                  className={`px-3 py-1.5 border-l border-gray-300 ${
                    mode === 'text'
                      ? 'bg-primary text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40'
                  }`}
                >
                  Plain text
                </button>
              </div>
            </div>

            <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
              {mode === 'html' && reply.html ? (
                <iframe
                  title='Reply HTML'
                  srcDoc={reply.html}
                  sandbox=''
                  className='w-full h-[60vh] bg-white rounded shadow border border-gray-200'
                />
              ) : reply.text ? (
                <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800'>
                  {reply.text}
                </pre>
              ) : (
                <p className='text-sm text-gray-500 italic'>
                  No body returned by Resend for this reply.
                </p>
              )}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function PreviewDialog({
  result,
  mode,
  onModeChange,
  onClose,
}: {
  result: CoachOutreachPreviewResponse | null;
  mode: 'html' | 'text';
  onModeChange: (m: 'html' | 'text') => void;
  onClose: () => void;
}) {
  if (!result) return null;
  const listUnsub = result.headers['List-Unsubscribe'] ?? '';
  const listUnsubPost = result.headers['List-Unsubscribe-Post'] ?? '';
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'
      onClick={onClose}
    >
      <div
        className='bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col'
        onClick={(e) => e.stopPropagation()}
      >
        <header className='flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4'>
          <div className='min-w-0'>
            <h2 className='text-lg font-semibold text-gray-900'>
              Preview &mdash; what the recipient will see
            </h2>
            <p className='mt-0.5 text-xs text-gray-500'>
              Rendered through the same path as the Celery sender. Headers
              and footer are byte-identical to a live send.
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-700 hover:bg-gray-100'
            aria-label='Close preview'
          >
            Close
          </button>
        </header>

        <div className='border-b border-gray-200 bg-gray-50 px-5 py-3 text-xs text-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1'>
          <div className='flex gap-2'>
            <span className='font-semibold text-gray-500 w-16'>From</span>
            <span className='truncate'>{result.from_email}</span>
          </div>
          <div className='flex gap-2'>
            <span className='font-semibold text-gray-500 w-16'>To</span>
            <span className='truncate'>
              {result.to_email || (
                <em className='text-amber-700'>
                  no email on file (would skip at send time)
                </em>
              )}
            </span>
          </div>
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>Subject</span>
            <span className='truncate'>{result.subject}</span>
          </div>
          {result.reply_to ? (
            <div className='flex gap-2'>
              <span className='font-semibold text-gray-500 w-16'>
                Reply-To
              </span>
              <span className='truncate'>{result.reply_to}</span>
            </div>
          ) : null}
          <div className='flex gap-2 sm:col-span-2'>
            <span className='font-semibold text-gray-500 w-16'>
              Unsubscribe
            </span>
            <a
              href={result.unsubscribe_url}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary hover:underline truncate'
              title={result.unsubscribe_url}
            >
              {result.unsubscribe_url}
            </a>
          </div>
          {listUnsub ? (
            <div className='flex gap-2 sm:col-span-2'>
              <span className='font-semibold text-gray-500 w-16'>Headers</span>
              <span className='truncate text-gray-600'>
                <code className='text-[11px]'>List-Unsubscribe: {listUnsub}</code>
                {listUnsubPost ? (
                  <>
                    <br />
                    <code className='text-[11px]'>
                      List-Unsubscribe-Post: {listUnsubPost}
                    </code>
                  </>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>

        <div className='border-b border-gray-200 px-5 pt-3'>
          <div className='inline-flex rounded-md border border-gray-300 overflow-hidden text-sm'>
            <button
              type='button'
              onClick={() => onModeChange('html')}
              className={`px-3 py-1.5 ${
                mode === 'html'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              HTML
            </button>
            <button
              type='button'
              onClick={() => onModeChange('text')}
              className={`px-3 py-1.5 border-l border-gray-300 ${
                mode === 'text'
                  ? 'bg-primary text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Plain text
            </button>
          </div>
        </div>

        <div className='flex-1 min-h-0 overflow-auto bg-gray-100 p-4'>
          {mode === 'html' ? (
            <iframe
              title='Email HTML preview'
              srcDoc={result.html}
              sandbox=''
              className='w-full h-[60vh] bg-white rounded shadow border border-gray-200'
            />
          ) : (
            <pre className='w-full bg-white rounded shadow border border-gray-200 p-4 text-sm font-mono whitespace-pre-wrap text-gray-800'>
              {result.text}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/admin/coach-outreach')({
  component: CoachOutreachAdminPage,
});
